import type { RootState } from '@/components/WellLoggingOperations/types';
import { Button } from '@/shared/MiButton';
import Modal from '@/shared/Modal';
import type { Permission } from '@/store/geo-test/types';
import { PERMISSION_TITLES, PERMISSIONS } from '@/store/geo-test/types';
import { CheckCircleOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { AgGridReact } from 'ag-grid-react';
import { Input, Select, Tooltip, type ModalProps } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState, type FC, type KeyboardEventHandler } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { GeoUserProfile } from '../types';
import type { ColDef, ICellRendererParams, RowClickedEvent } from 'ag-grid-community';
import type { SelectProps } from 'antd/lib';
import { ProfileInfoDrawer } from './ProfileInfo';
import { FullScreenLoader } from '@/shared/Loading/FullScreenLoader';
import { SaveProfileModal } from '../SaveProfileModal';
import type { MessageInstance } from 'antd/es/message/interface';
import { toPermission } from '../utils';

const ActiveCell: FC<ICellRendererParams<GeoUserProfile>> = ({ data }) => {
    const { activeUserProfile } = useSelector((store: RootState) => store.geoTest);
    if (!data) {
        return null;
    }

    const { id } = data;

    if (activeUserProfile === id) {
        return (
            <Tooltip title="Активный профиль">
                <CheckCircleOutlined className="text-green-500" />
            </Tooltip>
        );
    }

    return '';
};

const PermissionCell: FC<ICellRendererParams<GeoUserProfile>> = ({ data }) => {
    if (!data) {
        return null;
    }

    const { readOnly, isPrivate } = data;
    const permission = toPermission(isPrivate, readOnly);

    return PERMISSION_TITLES[permission];
};

const COLUMNS: Array<ColDef<GeoUserProfile>> = [
    {
        headerName: '',
        width: 30,
        cellRenderer: ActiveCell,
    },
    {
        field: 'profileName',
        headerName: 'Наименование',
        tooltipField: 'profileName',
        flex: 1,
    },
    {
        field: 'profileOwner',
        headerName: 'Пользователь',
        tooltipField: 'profileOwner',
        width: 150,
    },
    {
        headerName: 'Тип доступа',
        width: 150,
        cellRenderer: PermissionCell,
    },
];

export const UserProfilesModal: FC<ModalProps & { messageApi: MessageInstance }> = ({ messageApi, ...props }) => {
    const handleKeyDown: KeyboardEventHandler<HTMLElement> = e => {
        if (e.key === 'Enter') {
            props.onClose;
        }
    };

    const gridRef = useRef<AgGridReact<GeoUserProfile>>(null);

    const { userProfiles, isUserProfilesLoading } = useSelector((store: RootState) => store.geoTest);

    const dispatch = useDispatch();
    const { loadUserProfiles, loadGeoData, applyProfile } = dispatch.geoTest;

    const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>(Array.from(PERMISSIONS));
    const [localActiveProfile, setLocalActiveProfile] = useState<number | null>(null);

    const [openInfo, setOpenInfo] = useState<boolean>(false);
    const [saveModal, setSaveModal] = useState<boolean>(false);
    const profile = userProfiles.find(({ id }) => localActiveProfile === id);

    useEffect(() => {
        loadUserProfiles();
    }, []);

    const filteredProfiles = useMemo(
        () =>
            userProfiles.filter(({ isPrivate, readOnly }) =>
                selectedPermissions.includes(toPermission(isPrivate, readOnly)),
            ),
        [userProfiles, selectedPermissions],
    );

    const defaultColDef = {
        editable: false,
        resizable: false,
    };

    const onApply = async () => {
        if (profile) {
            applyProfile(profile);
            dispatch.lists.selectItem(null);
            await loadGeoData({ messageApi, profileId: localActiveProfile });
            props.onCancel?.({} as React.MouseEvent<HTMLButtonElement>);
        }
    };

    const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        gridRef.current?.api.setGridOption('quickFilterText', value);
    };

    const handleRowClick = (event: RowClickedEvent<GeoUserProfile>) => {
        const { id } = event.data!;
        setOpenInfo(true);
        setLocalActiveProfile(id);
    };

    const options: SelectProps['options'] = PERMISSIONS.map(value => ({
        value,
        label: PERMISSION_TITLES[value],
    }));

    const onClose = () => {
        setOpenInfo(false);
    };

    const onCloseSaveModal = () => {
        setSaveModal(false);
    };

    const afterOpenChange = (open: boolean) => {
        !open && setLocalActiveProfile(null);
    };

    const getRowStyle = useCallback(
        ({ data }: { data?: GeoUserProfile }) => {
            if (data?.id === localActiveProfile) {
                return { background: '#E6F2FE' };
            }
        },
        [localActiveProfile],
    );

    return (
        <Modal onOk={onApply} okButtonProps={{ onKeyDown: handleKeyDown }} {...props}>
            {saveModal && (
                <SaveProfileModal
                    title="Сохранение"
                    width={400}
                    open={saveModal}
                    onClose={onCloseSaveModal}
                    onCancel={onCloseSaveModal}
                    messageApi={messageApi}
                />
            )}
            <div className="h-[500px] flex flex-col text-xs font-semibold pt-0">
                {isUserProfilesLoading ? (
                    <FullScreenLoader />
                ) : (
                    <>
                        <div className="relative overflow-hidden flex flex-col flex-1">
                            <div className="flex items-center justify-between p-1 px-4">
                                <div className="flex items-center gap-4">
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        size="small"
                                        style={{ height: 24 }}
                                        styles={{
                                            popup: {
                                                root: { fontSize: 12 },
                                            },
                                        }}
                                        className="w-56"
                                        placeholder="Выберите..."
                                        defaultValue={selectedPermissions}
                                        onChange={setSelectedPermissions}
                                        options={options}
                                        maxTagCount="responsive"
                                    />
                                    <Input
                                        className="w-40"
                                        size="small"
                                        placeholder="Поиск по названию и пользователю"
                                        prefix={<SearchOutlined />}
                                        allowClear
                                        onChange={onSearch}
                                    />
                                </div>

                                <Button
                                    type="text"
                                    onClick={() => setSaveModal(true)}
                                    icon={<PlusOutlined className="!text-base" />}
                                />
                            </div>
                            <div className="w-full h-full flex">
                                <div className="perforation-table well-logging-table bordered-header-top h-full w-full">
                                    <AgGridReact
                                        ref={gridRef}
                                        rowData={filteredProfiles}
                                        columnDefs={COLUMNS}
                                        defaultColDef={defaultColDef}
                                        headerHeight={32}
                                        rowHeight={32}
                                        enableBrowserTooltips={true}
                                        onRowClicked={handleRowClick}
                                        getRowStyle={getRowStyle}
                                    />
                                </div>
                                <ProfileInfoDrawer
                                    selectedId={localActiveProfile}
                                    onClose={onClose}
                                    afterOpenChange={afterOpenChange}
                                    open={openInfo}
                                    messageApi={messageApi}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};
